import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import type OlMap from 'ol/Map';
import type Overlay from 'ol/Overlay';
import type { FeatureLike } from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type LineString from 'ol/geom/LineString';
import type Polygon from 'ol/geom/Polygon';
import type VectorSource from 'ol/source/Vector';
import type Style from 'ol/style/Style';
import { FILE_ANNOTATIONS_QUERY, FILE_MEASUREMENTS_QUERY, SHARED_FILE_ANNOTATIONS_QUERY, SHARED_FILE_MEASUREMENTS_QUERY, SHARED_LINK_QUERY, SHARED_NOTES_ACCESS_QUERY } from '../../graphql/queries';
import {
  CREATE_ANNOTATION_MUTATION,
  REPLY_ANNOTATION_MUTATION,
  RESOLVE_ANNOTATION_MUTATION,
  DELETE_ANNOTATION_MUTATION,
  ADD_ANNOTATION_ATTACHMENT_MUTATION,
  DELETE_ANNOTATION_ATTACHMENT_MUTATION,
  SHARED_CREATE_ANNOTATION_MUTATION,
  SHARED_REPLY_ANNOTATION_MUTATION,
  CREATE_MEASUREMENT_MUTATION,
  CREATE_AREA_MEASUREMENT_MUTATION,
  DELETE_MEASUREMENT_MUTATION,
  SHARED_CREATE_MEASUREMENT_MUTATION,
  SHARED_CREATE_AREA_MEASUREMENT_MUTATION,
  SHARED_DELETE_MEASUREMENT_MUTATION,
} from '../../graphql/mutations';
import Modal from '../../components/common/Modal';
import { putWithProgress } from '../../utils/putWithProgress';
import {
  coordinatesExtent,
  formatArea,
  formatDistance,
  groundArea,
  groundFrame,
  groundLength,
  measurementCoordinates,
  measurementLabel,
  measurementValue,
  toMeasurementPoints,
  type MeasureKind,
  type MeasurePoint,
  type OrthoGeoKeys,
} from '../../utils/orthoMeasure';
import styles from './OrthoViewer.module.css';
import Icon from '../../components/common/Icon';

const TOKEN_KEY = 'geodraft_token';
const GUEST_NAME_KEY = 'geodraft_guest_name';

const FILE_QUERY = gql`
  query OrthoFile($id: ID!) {
    file(id: $id) {
      id
      originalName
      fileSize
      status
    }
  }
`;

const formatSize = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// ── Notes (same shapes and helpers as SplatViewer) ───────────────────────────

interface AnnotationPosition { x: number; y: number; z: number }
interface NoteAttachment {
  id: string; fileName: string; mimeType: string; fileSize: number; createdAt: string;
  url: string | null; downloadUrl: string | null;
  uploadedBy?: { id: string; firstName: string; lastName: string } | null;
}
interface NoteReply {
  id: string; createdBy?: { id: string; firstName: string; lastName: string } | null;
  guestName?: string | null; text: string; createdAt: string;
}
interface Note {
  id: string;
  createdBy?: { id: string; firstName: string; lastName: string } | null;
  guestName?: string | null;
  position: AnnotationPosition;
  text: string;
  status: string;
  replies: NoteReply[];
  attachments: NoteAttachment[];
  createdAt: string;
}

const authorName = (x: { guestName?: string | null; createdBy?: { firstName: string; lastName: string } | null }) =>
  x.guestName ? `${x.guestName} (guest)` : `${x.createdBy?.firstName ?? ''} ${x.createdBy?.lastName ?? ''}`.trim();
const formatTime = (s: string) =>
  new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const formatBytes = (b: number) =>
  b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

const PDF_MIME = 'application/pdf';
const isPdfFile = (file: File) => file.type === PDF_MIME || /\.pdf$/i.test(file.name);
const isPdfAttachment = (att: NoteAttachment) => att.mimeType === PDF_MIME || /\.pdf$/i.test(att.fileName);

// ── Measurements ─────────────────────────────────────────────────────────────

interface Measurement {
  id: string;
  kind: string;
  createdBy?: { id: string; firstName: string; lastName: string } | null;
  guestName?: string | null;
  // Share links only: a guest of this link made it, so a guest may delete it.
  fromThisLink?: boolean;
  start?: MeasurePoint | null;
  end?: MeasurePoint | null;
  distance?: number | null;
  points?: MeasurePoint[] | null;
  area?: number | null;
  createdAt: string;
}

const measureMeta = (m: Measurement) => [
  m.createdBy || m.guestName ? authorName(m) : m.id.startsWith('local-') ? 'Not saved' : '',
  formatTime(m.createdAt),
].filter(Boolean).join(' · ');

const loadMeasureKit = async () => {
  const [
    vectorLayer, vectorSource, draw, feature, lineString, polygon, point, multiPoint,
    style, stroke, fill, text, circle, sphere, condition,
  ] = await Promise.all([
    import('ol/layer/Vector'),
    import('ol/source/Vector'),
    import('ol/interaction/Draw'),
    import('ol/Feature'),
    import('ol/geom/LineString'),
    import('ol/geom/Polygon'),
    import('ol/geom/Point'),
    import('ol/geom/MultiPoint'),
    import('ol/style/Style'),
    import('ol/style/Stroke'),
    import('ol/style/Fill'),
    import('ol/style/Text'),
    import('ol/style/Circle'),
    import('ol/sphere'),
    import('ol/events/condition'),
  ]);
  return {
    VectorLayer: vectorLayer.default,
    VectorSource: vectorSource.default,
    Draw: draw.default,
    Feature: feature.default,
    LineString: lineString.default,
    Polygon: polygon.default,
    Point: point.default,
    MultiPoint: multiPoint.default,
    Style: style.default,
    Stroke: stroke.default,
    Fill: fill.default,
    Text: text.default,
    CircleStyle: circle.default,
    sphereLength: sphere.getLength,
    sphereArea: sphere.getArea,
    noModifierKeys: condition.noModifierKeys,
    primaryAction: condition.primaryAction,
    never: condition.never,
  };
};
type MeasureKit = Awaited<ReturnType<typeof loadMeasureKit>>;
type MeasureStyleFn = (feature: FeatureLike) => Style[];

const MEASURE_COLOR = '#0061ff';
const MEASURE_FONT = '600 12px Inter, system-ui, sans-serif';

// White halo under a blue line so a measurement reads on bright concrete and
// dark vegetation alike; the value label sits on its own plate for the same reason.
const makeMeasureStyles = (kit: MeasureKit) => {
  const halo = new kit.Style({ stroke: new kit.Stroke({ color: 'rgba(255, 255, 255, 0.92)', width: 5.5 }) });
  const haloActive = new kit.Style({ stroke: new kit.Stroke({ color: 'rgba(255, 255, 255, 0.95)', width: 7.5 }) });
  const stroke = new kit.Stroke({ color: MEASURE_COLOR, width: 2.5 });
  const strokeActive = new kit.Stroke({ color: MEASURE_COLOR, width: 4 });
  const strokePending = new kit.Stroke({ color: MEASURE_COLOR, width: 2.5, lineDash: [6, 5] });
  const fill = new kit.Fill({ color: 'rgba(0, 97, 255, 0.16)' });
  const fillActive = new kit.Fill({ color: 'rgba(0, 97, 255, 0.26)' });
  const vertex = new kit.CircleStyle({
    radius: 4,
    fill: new kit.Fill({ color: '#ffffff' }),
    stroke: new kit.Stroke({ color: MEASURE_COLOR, width: 2 }),
  });
  const cursor = new kit.Style({
    image: new kit.CircleStyle({
      radius: 5,
      fill: new kit.Fill({ color: 'rgba(255, 255, 255, 0.85)' }),
      stroke: new kit.Stroke({ color: MEASURE_COLOR, width: 2 }),
    }),
  });
  const sketchLine = new kit.Style({ stroke });
  const sketchArea = new kit.Style({
    fill,
    stroke: new kit.Stroke({ color: MEASURE_COLOR, width: 1.5, lineDash: [5, 5] }),
  });

  const label = (geometry: LineString | Polygon, value: string, active: boolean) => new kit.Style({
    geometry: geometry.getType() === 'Polygon'
      ? (geometry as Polygon).getInteriorPoint()
      : new kit.Point((geometry as LineString).getCoordinateAt(0.5)),
    text: new kit.Text({
      text: value,
      font: MEASURE_FONT,
      fill: new kit.Fill({ color: active ? '#ffffff' : '#131b2e' }),
      backgroundFill: new kit.Fill({ color: active ? MEASURE_COLOR : 'rgba(255, 255, 255, 0.94)' }),
      backgroundStroke: new kit.Stroke({ color: active ? MEASURE_COLOR : 'rgba(19, 27, 46, 0.16)', width: 1 }),
      padding: [3, 6, 2, 6],
    }),
    zIndex: 2,
  });

  const saved: MeasureStyleFn = (feature) => {
    const geometry = feature.getGeometry() as LineString | Polygon | undefined;
    if (!geometry) return [];
    const active = !!feature.get('active');
    const isArea = geometry.getType() === 'Polygon';
    const vertices = isArea
      ? (geometry as Polygon).getCoordinates()[0] ?? []
      : (geometry as LineString).getCoordinates();
    return [
      active ? haloActive : halo,
      new kit.Style({
        stroke: feature.get('pending') ? strokePending : active ? strokeActive : stroke,
        ...(isArea ? { fill: active ? fillActive : fill } : {}),
      }),
      new kit.Style({ image: vertex, geometry: new kit.MultiPoint(vertices), zIndex: 1 }),
      label(geometry, feature.get('label'), active),
    ];
  };

  const sketch = (
    kind: MeasureKind,
    lengthOf: (g: LineString) => number,
    areaOf: (g: Polygon) => number,
  ): MeasureStyleFn => (feature) => {
    const geometry = feature.getGeometry() as Geometry | undefined;
    const type = geometry?.getType();
    if (type === 'Point') return [cursor];
    if (type === 'LineString') {
      const metres = kind === 'distance' ? lengthOf(geometry as LineString) : 0;
      return metres > 0
        ? [halo, sketchLine, label(geometry as LineString, formatDistance(metres), false)]
        : [halo, sketchLine];
    }
    if (type === 'Polygon') {
      const squareMetres = areaOf(geometry as Polygon);
      return squareMetres > 0
        ? [sketchArea, label(geometry as Polygon, formatArea(squareMetres), false)]
        : [sketchArea];
    }
    return [];
  };

  return { saved, sketch };
};

type OlHandle = {
  map: OlMap;
  OverlayCls: typeof Overlay;
  kit: MeasureKit;
  measureSource: VectorSource;
  measureStyles: ReturnType<typeof makeMeasureStyles>;
  lengthOf: (g: LineString) => number;
  areaOf: (g: Polygon) => number;
  nativeResolution?: number;
};

type OrthoMeta = {
  width?: number;
  height?: number;
  geoTransform?: number[] | null;
  crs?: string | null;
};

type OrthoViewerProps = {
  fileId?: string;
  embedded?: boolean;
};

type Stage = 'checking' | 'processing' | 'loading' | 'ready' | 'failed';

// Gigapixel orthomosaic viewer. The conversion job bakes the upload into a
// Cloud-Optimized GeoTIFF; OpenLayers reads it with HTTP Range requests —
// overview levels zoomed out, native JPEG tiles zoomed in — so a 7 GB survey
// opens in seconds and only the bytes the camera looks at ever move. The ol
// modules are imported dynamically so the chunk loads with this viewer, not
// with the app shell.
//
// Notes follow the Gaussian viewer's system one-to-one — same GraphQL
// operations, same rail design, same PDF-attachment pipeline. The only
// difference is the marker mechanics: instead of a postMessage protocol into a
// 3D iframe, notes anchor at map coordinates (x = easting, y = northing,
// z = 0) and render as ol Overlays, which the map projects for free.
//
// Measurements share that frame: points are stored as map coordinates with
// z = 0, and distance / area in metres measured in the map projection, so the
// Scene can redraw them through the ortho layer's own map ↔ world transform.
const OrthoViewer = ({ fileId: fileIdProp, embedded = false }: OrthoViewerProps) => {
  const { fileId: fileIdParam, token: shareToken } = useParams<{ projectId?: string; fileId: string; token?: string }>();
  const fileId = fileIdProp ?? fileIdParam;
  const isShare = !!shareToken;
  const navigate = useNavigate();

  const mapEl = useRef<HTMLDivElement>(null);
  const olRef = useRef<OlHandle | null>(null);
  const markersRef = useRef<Overlay[]>([]);
  const [stage, setStage] = useState<Stage>('checking');
  const [error, setError] = useState('');
  const [progress, setProgress] = useState<{ stage?: string; pct?: number } | null>(null);
  const [meta, setMeta] = useState<OrthoMeta | null>(null);
  const [rotationDeg, setRotationDeg] = useState(0);

  // ── Notes state (mirrors SplatViewer) ──
  const [annotationMode, setAnnotationMode] = useState(false);
  const annotationModeRef = useRef(false);
  const [pendingPos, setPendingPos] = useState<AnnotationPosition | null>(null);
  const [pendingText, setPendingText] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [notesCollapsed, setNotesCollapsed] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [guestName, setGuestName] = useState(() => localStorage.getItem(GUEST_NAME_KEY) ?? '');
  const updateGuestName = (v: string) => {
    setGuestName(v);
    try { localStorage.setItem(GUEST_NAME_KEY, v); } catch { /* private mode */ }
  };
  const activeCardRef = useRef<HTMLDivElement>(null);
  const [uploads, setUploads] = useState<Record<string, { annotationId: string; name: string; pct: number }>>({});
  const [attachError, setAttachError] = useState('');
  const [viewing, setViewing] = useState<NoteAttachment | null>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const attachTargetRef = useRef<string | null>(null);

  // ── Measurement state ──
  const [measureKind, setMeasureKind] = useState<MeasureKind | null>(null);
  const measureKindRef = useRef<MeasureKind | null>(null);
  const [lastMeasure, setLastMeasure] = useState('');
  const [localMeasures, setLocalMeasures] = useState<Measurement[]>([]);
  const [savingMeasures, setSavingMeasures] = useState<Measurement[]>([]);
  const [hiddenMeasures, setHiddenMeasures] = useState<Set<string>>(() => new Set());
  const [activeMeasureId, setActiveMeasureId] = useState<string | null>(null);
  const [measuresCollapsed, setMeasuresCollapsed] = useState(false);
  const [measureError, setMeasureError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Measurement | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [deletingMeasure, setDeletingMeasure] = useState(false);
  const activeMeasureCardRef = useRef<HTMLDivElement>(null);
  const measuredRef = useRef<(kind: MeasureKind, geometry: LineString | Polygon) => void>();

  const { data } = useQuery(FILE_QUERY, { variables: { id: fileId }, skip: !fileId || isShare });
  // Share visitors have no account — file info comes from the public share-link query.
  const { data: shareData } = useQuery(SHARED_LINK_QUERY, { variables: { token: shareToken }, skip: !isShare });
  const fileInfo = isShare
    ? shareData?.sharedLink?.files?.find((f: { id: string }) => f.id === fileId)
    : data?.file;

  const { data: annData, refetch: refetchAnn } = useQuery(FILE_ANNOTATIONS_QUERY, {
    variables: { fileId },
    skip: !fileId || isShare,
    fetchPolicy: 'cache-and-network',
  });
  const { data: sharedAnnData, refetch: refetchSharedAnn } = useQuery(SHARED_FILE_ANNOTATIONS_QUERY, {
    variables: { token: shareToken, fileId },
    skip: !isShare || !fileId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: accessData } = useQuery(SHARED_NOTES_ACCESS_QUERY, {
    variables: { token: shareToken, fileId },
    skip: !isShare || !fileId,
  });
  const canWriteNotes = !isShare || !!accessData?.sharedNotesAccess;
  const annotations: Note[] = (isShare ? sharedAnnData?.sharedFileAnnotations : annData?.fileAnnotations) ?? [];
  const refetchAnnotations = useCallback(async (): Promise<Note[]> => {
    try {
      if (isShare) {
        const res = await refetchSharedAnn();
        return (res.data?.sharedFileAnnotations ?? []) as Note[];
      }
      const res = await refetchAnn();
      return (res.data?.fileAnnotations ?? []) as Note[];
    } catch {
      return [];
    }
  }, [isShare, refetchAnn, refetchSharedAnn]);

  const onNoteError = (err: { message: string }) => setNoteError(err.message);
  const onCreated = () => { setPendingPos(null); setPendingText(''); setNoteError(''); refetchAnnotations(); };
  const onReplied = () => { setReplyText(''); setNoteError(''); refetchAnnotations(); };
  const [createAnnotation, { loading: creatingOwn }] = useMutation(CREATE_ANNOTATION_MUTATION, {
    onCompleted: onCreated,
    onError: onNoteError,
  });
  const [replyToAnnotation, { loading: replyingOwn }] = useMutation(REPLY_ANNOTATION_MUTATION, {
    onCompleted: onReplied,
    onError: onNoteError,
  });
  const [sharedCreateAnnotation, { loading: creatingGuest }] = useMutation(SHARED_CREATE_ANNOTATION_MUTATION, {
    onCompleted: onCreated,
    onError: onNoteError,
  });
  const [sharedReplyToAnnotation, { loading: replyingGuest }] = useMutation(SHARED_REPLY_ANNOTATION_MUTATION, {
    onCompleted: onReplied,
    onError: onNoteError,
  });
  const creating = creatingOwn || creatingGuest;
  const replying = replyingOwn || replyingGuest;
  const [resolveAnnotation] = useMutation(RESOLVE_ANNOTATION_MUTATION, { onError: onNoteError });
  const [deleteAnnotation] = useMutation(DELETE_ANNOTATION_MUTATION, {
    onCompleted: () => { setExpandedId(null); refetchAnnotations(); },
    onError: onNoteError,
  });
  const [addAttachment] = useMutation(ADD_ANNOTATION_ATTACHMENT_MUTATION);
  const [deleteAttachment] = useMutation(DELETE_ANNOTATION_ATTACHMENT_MUTATION, {
    onCompleted: () => { refetchAnnotations(); },
  });

  // Every share link shows the saved measurements. A visitor's own are saved
  // only when the link lets guests add them; otherwise they live in this page.
  const guestMeasures = isShare && !!shareData?.sharedLink?.allowOrthoMeasurements;
  const { data: measData, refetch: refetchMeas } = useQuery(FILE_MEASUREMENTS_QUERY, {
    variables: { fileId },
    skip: !fileId || isShare,
    fetchPolicy: 'cache-and-network',
  });
  const { data: sharedMeasData, refetch: refetchSharedMeas } = useQuery(SHARED_FILE_MEASUREMENTS_QUERY, {
    variables: { token: shareToken, fileId },
    skip: !fileId || !isShare,
    fetchPolicy: 'cache-and-network',
  });
  const measurements = useMemo<Measurement[]>(
    () => (isShare ? [...(sharedMeasData?.sharedFileMeasurements ?? []), ...localMeasures] : measData?.fileMeasurements ?? [])
      .filter((m: Measurement) => measurementCoordinates(m) !== null),
    [isShare, localMeasures, measData, sharedMeasData],
  );
  const refetchMeasurements = async () => {
    try {
      if (isShare) await refetchSharedMeas();
      else await refetchMeas();
    } catch { /* the list keeps its last state */ }
  };
  const isLocalMeasure = (m: Measurement) => m.id.startsWith('local-');
  const canDeleteMeasurement = (m: Measurement) => !isShare || isLocalMeasure(m) || !!m.fromThisLink;
  const [createMeasurement] = useMutation(CREATE_MEASUREMENT_MUTATION);
  const [createAreaMeasurement] = useMutation(CREATE_AREA_MEASUREMENT_MUTATION);
  const [deleteMeasurement] = useMutation(DELETE_MEASUREMENT_MUTATION);
  const [sharedCreateMeasurement] = useMutation(SHARED_CREATE_MEASUREMENT_MUTATION);
  const [sharedCreateAreaMeasurement] = useMutation(SHARED_CREATE_AREA_MEASUREMENT_MUTATION);
  const [sharedDeleteMeasurement] = useMutation(SHARED_DELETE_MEASUREMENT_MUTATION);

  useEffect(() => { annotationModeRef.current = annotationMode; }, [annotationMode]);
  useEffect(() => { measureKindRef.current = measureKind; }, [measureKind]);

  // ── COG loading ──
  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    let timer: number | undefined;

    const init = async (desc: { url: string; metaUrl: string }) => {
      setStage('loading');
      try {
        fetch(desc.metaUrl)
          .then((r) => (r.ok ? r.json() : null))
          .then((m) => { if (!cancelled && m) setMeta(m); })
          .catch(() => {});
        const [
          { default: OlMapCls },
          { default: View },
          { default: WebGLTileLayer },
          { default: GeoTIFF },
          { defaults: controlDefaults },
          { default: ScaleLine },
          { default: OverlayCls },
          { default: PointerInteraction },
          kit,
        ] = await Promise.all([
          import('ol/Map'),
          import('ol/View'),
          import('ol/layer/WebGLTile'),
          import('ol/source/GeoTIFF'),
          import('ol/control/defaults'),
          import('ol/control/ScaleLine'),
          import('ol/Overlay'),
          import('ol/interaction/Pointer'),
          loadMeasureKit(),
        ]);
        await import('ol/ol.css');
        if (cancelled || !mapEl.current) return;

        class OrthoTiffSource extends GeoTIFF {
          geoKeys: OrthoGeoKeys | null = null;

          async determineProjection(sources: Parameters<InstanceType<typeof GeoTIFF>['determineProjection']>[0]) {
            for (const image of sources[0] ?? []) {
              try {
                const keys = image.getGeoKeys();
                if (keys) { this.geoKeys = keys as OrthoGeoKeys; break; }
              } catch { /* a malformed key directory reads as none */ }
            }
            await super.determineProjection(sources);
          }
        }
        const source = new OrthoTiffSource({
          sources: [{ url: desc.url }],
          convertToRGB: true,
          interpolate: true,
        });
        const measureStyles = makeMeasureStyles(kit);
        const measureSource = new kit.VectorSource();
        const measureLayer = new kit.VectorLayer({
          source: measureSource,
          style: measureStyles.saved,
          updateWhileAnimating: true,
          updateWhileInteracting: true,
        });
        const map = new OlMapCls({
          target: mapEl.current,
          layers: [new WebGLTileLayer({ source }), measureLayer],
          controls: controlDefaults({ attribution: false, rotate: false })
            .extend([new ScaleLine({ units: 'metric' })]),
        });
        map.on('singleclick', (evt) => {
          if (annotationModeRef.current) {
            setPendingPos({ x: evt.coordinate[0], y: evt.coordinate[1], z: 0 });
            setAnnotationMode(false);
            setNotesCollapsed(false);
            return;
          }
          if (measureKindRef.current) return;
          const id = map.forEachFeatureAtPixel(
            evt.pixel,
            (f) => (f.get('pending') ? undefined : (f.get('measureId') as string | undefined)),
            { layerFilter: (l) => l === measureLayer, hitTolerance: 5 },
          );
          setActiveMeasureId(id ?? null);
          if (id) setMeasuresCollapsed(false);
        });
        // Right-button drag rotates around the view center — same angle math
        // as ol's own Alt+Shift DragRotate (which stays available too). The
        // primary button keeps panning untouched.
        let lastAngle: number | undefined;
        map.addInteraction(new (class extends PointerInteraction {
          handleDownEvent(evt: import('ol/MapBrowserEvent').default<PointerEvent>): boolean {
            if (evt.originalEvent.button !== 2) return false;
            lastAngle = undefined;
            return true;
          }

          handleDragEvent(evt: import('ol/MapBrowserEvent').default<PointerEvent>): void {
            const size = evt.map.getSize();
            if (!size) return;
            const theta = Math.atan2(size[1] / 2 - evt.pixel[1], evt.pixel[0] - size[0] / 2);
            if (lastAngle !== undefined) evt.map.getView().adjustRotation(-(theta - lastAngle));
            lastAngle = theta;
          }

          handleUpEvent(): boolean {
            return false;
          }
        })());
        const vo = await source.getView();
        if (cancelled) { map.setTarget(undefined); return; }
        // The source view locks zoom to the pyramid levels; a continuous view
        // with 8x over-zoom reads better for measuring detail on site photos.
        const native = vo.resolutions?.[vo.resolutions.length - 1]
          ?? (vo.extent && meta?.width ? (vo.extent[2] - vo.extent[0]) / meta.width : undefined);
        map.setView(new View({
          projection: vo.projection,
          center: vo.center,
          zoom: vo.zoom,
          resolution: vo.resolution,
          resolutions: undefined,
          extent: vo.extent,
          constrainOnlyCenter: true,
          showFullExtent: true,
          ...(native ? { minResolution: native / 8 } : {}),
        }));
        if (vo.extent) map.getView().fit(vo.extent, { padding: [24, 24, 24, 24] });
        const view = map.getView();
        view.on('change:rotation', () => {
          if (!cancelled) setRotationDeg((view.getRotation() * 180) / Math.PI);
        });
        map.once('rendercomplete', () => { if (!cancelled) setStage('ready'); });
        const frame = groundFrame(source.geoKeys);
        olRef.current = {
          map,
          OverlayCls,
          kit,
          measureSource,
          measureStyles,
          nativeResolution: native,
          lengthOf: (g) => (frame.kind === 'sphere'
            ? kit.sphereLength(g, { projection: 'EPSG:4326' })
            : groundLength(g.getCoordinates(), frame)),
          areaOf: (g) => (frame.kind === 'sphere'
            ? kit.sphereArea(g, { projection: 'EPSG:4326' })
            : groundArea(g.getCoordinates()[0] ?? [], frame)),
        };
      } catch (err) {
        if (!cancelled) {
          setStage('failed');
          setError(err instanceof Error ? err.message : 'Failed to open the orthomosaic');
        }
      }
    };

    const poll = async () => {
      try {
        const params = new URLSearchParams();
        if (import.meta.env.DEV) params.set('mode', 'proxy');
        if (shareToken) params.set('shareToken', shareToken);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const res = await fetch(`/api/ortho/${fileId}${qs}`, {
          headers: shareToken
            ? undefined
            : { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const desc = await res.json();
        if (cancelled) return;
        if (desc.status === 'ready' && desc.url) { init(desc); return; }
        if (desc.status === 'error') {
          setStage('failed');
          setError('Conversion failed — try re-uploading the file');
          return;
        }
        setStage('processing');
        setProgress(desc.progress ?? null);
        timer = window.setTimeout(poll, 5000);
      } catch (err) {
        if (!cancelled) {
          setStage('failed');
          setError(err instanceof Error ? err.message : 'Failed to load');
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      olRef.current?.map.setTarget(undefined);
      olRef.current = null;
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  // Crosshair while placing a note or measuring.
  useEffect(() => {
    if (mapEl.current) mapEl.current.style.cursor = annotationMode || measureKind ? 'crosshair' : '';
  }, [annotationMode, measureKind]);

  // ── Marker sync (the ol counterpart of the splat viewer's addMarker protocol) ──
  useEffect(() => {
    const ol = olRef.current;
    if (!ol || stage !== 'ready') return;
    markersRef.current.forEach((m) => ol.map.removeOverlay(m));
    markersRef.current = [];
    annotations.forEach((a, i) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = [
        styles.mapMarker,
        a.status === 'resolved' ? styles.mapMarkerResolved : '',
        a.id === activeId ? styles.mapMarkerActive : '',
      ].filter(Boolean).join(' ');
      el.textContent = String(i + 1);
      el.title = a.text.length > 80 ? `${a.text.slice(0, 80)}…` : a.text;
      el.onclick = () => {
        setActiveId(a.id);
        setExpandedId(a.id);
        setNotesCollapsed(false);
      };
      const overlay = new ol.OverlayCls({
        element: el,
        position: [a.position.x, a.position.y],
        positioning: 'bottom-center',
        stopEvent: true,
      });
      ol.map.addOverlay(overlay);
      markersRef.current.push(overlay);
    });
  }, [annotations, activeId, stage]);

  // Scroll the activated card into view (marker click → panel focus).
  useEffect(() => {
    if (activeId && activeCardRef.current) {
      activeCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeId]);

  // ── Measurement sync: saved ones are drawn from their stored map coordinates ──
  useEffect(() => {
    const ol = olRef.current;
    if (!ol || stage !== 'ready') return;
    const features = [...measurements, ...savingMeasures].flatMap((m) => {
      const coords = measurementCoordinates(m);
      if (!coords || hiddenMeasures.has(m.id)) return [];
      return [new ol.kit.Feature({
        geometry: m.kind === 'area' ? new ol.kit.Polygon([[...coords, coords[0]]]) : new ol.kit.LineString(coords),
        measureId: m.id,
        label: measurementValue(m),
        active: m.id === activeMeasureId,
        pending: savingMeasures.includes(m),
      })];
    });
    ol.measureSource.clear(true);
    ol.measureSource.addFeatures(features);
  }, [measurements, savingMeasures, hiddenMeasures, activeMeasureId, stage]);

  // The tool stays on after each measurement, so the next click starts the
  // next one — the same rhythm as the Scene's measure tools.
  useEffect(() => {
    const ol = olRef.current;
    if (!ol || stage !== 'ready' || !measureKind) return;
    const kind = measureKind;
    const draw = new ol.kit.Draw({
      type: kind === 'area' ? 'Polygon' : 'LineString',
      maxPoints: kind === 'distance' ? 2 : undefined,
      style: ol.measureStyles.sketch(kind, ol.lengthOf, ol.areaOf),
      // Left button only (the right button rotates), and no Shift-freehand.
      condition: (e) => ol.kit.noModifierKeys(e) && ol.kit.primaryAction(e),
      freehandCondition: ol.kit.never,
    });
    draw.on('drawend', (evt) => {
      const geometry = evt.feature.getGeometry() as LineString | Polygon | undefined;
      if (geometry) measuredRef.current?.(kind, geometry);
    });
    ol.map.addInteraction(draw);
    return () => {
      draw.abortDrawing();
      ol.map.removeInteraction(draw);
    };
  }, [measureKind, stage]);

  useEffect(() => {
    if (activeMeasureId && activeMeasureCardRef.current) {
      activeMeasureCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeMeasureId]);

  // Esc cancels pick mode or measuring, or closes the lightbox.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (viewing) setViewing(null);
      else if (annotationModeRef.current) setAnnotationMode(false);
      else if (measureKindRef.current) setMeasureKind(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing]);

  const rotateBy = (delta: number) => {
    const view = olRef.current?.map.getView();
    if (view) view.animate({ rotation: view.getRotation() + delta, duration: 200 });
  };
  const resetRotation = () => {
    const view = olRef.current?.map.getView();
    if (view) view.animate({ rotation: 0, duration: 250 });
  };

  const startAddNote = () => { setNoteError(''); setMeasureKind(null); setAnnotationMode(true); };
  const cancelAddNote = () => setAnnotationMode(false);

  const handleSaveAnnotation = () => {
    if (!fileId || !pendingPos || !pendingText.trim()) return;
    setNoteError('');
    if (isShare) {
      sharedCreateAnnotation({ variables: {
        token: shareToken, fileId, position: pendingPos, text: pendingText.trim(),
        guestName: guestName.trim() || null,
      } });
    } else {
      createAnnotation({ variables: { fileId, position: pendingPos, text: pendingText.trim() } });
    }
  };

  const handleReply = (annotationId: string) => {
    if (!replyText.trim()) return;
    setNoteError('');
    if (isShare) {
      sharedReplyToAnnotation({ variables: {
        token: shareToken, annotationId, text: replyText.trim(),
        guestName: guestName.trim() || null,
      } });
    } else {
      replyToAnnotation({ variables: { annotationId, text: replyText.trim() } });
    }
  };

  const handleCardClick = (id: string) => {
    setActiveId(id);
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // ── Attachments (same pipeline as SplatViewer: presign → S3 PUT → record) ──
  const uploadAttachments = async (annotationId: string, fileList: FileList) => {
    setAttachError('');
    const files = Array.from(fileList);
    const rejected = files.filter((f) => !isPdfFile(f));
    if (rejected.length) {
      setAttachError(`Only PDF files can be attached — skipped ${rejected.map((f) => f.name).join(', ')}`);
    }
    const token = localStorage.getItem(TOKEN_KEY) || '';
    for (const file of files.filter(isPdfFile)) {
      const key = crypto.randomUUID();
      setUploads((u) => ({ ...u, [key]: { annotationId, name: file.name, pct: 0 } }));
      try {
        const presignRes = await fetch(`/api/annotations/${annotationId}/attachments/presign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ fileName: file.name, fileSize: file.size }),
        });
        const presign = await presignRes.json();
        if (!presignRes.ok) throw new Error(presign.error || 'Could not prepare the upload');
        const { s3Key, uploadUrl } = presign as { s3Key: string; uploadUrl: string };
        try {
          await putWithProgress(uploadUrl, file, (pct) =>
            setUploads((u) => (u[key] ? { ...u, [key]: { ...u[key], pct } } : u)), PDF_MIME);
        } catch {
          // Origins the bucket CORS rejects PUT from (localhost) go through the
          // backend pass-through instead.
          const proxyRes = await fetch(`/api/annotations/attachments/upload?key=${encodeURIComponent(s3Key)}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': PDF_MIME },
            body: file,
          });
          if (!proxyRes.ok) {
            const body = await proxyRes.json().catch(() => ({}));
            throw new Error(body.error || 'Upload failed');
          }
        }
        await addAttachment({ variables: { annotationId, s3Key, fileName: file.name } });
      } catch (err) {
        setAttachError(`${file.name}: ${err instanceof Error ? err.message : 'Upload failed'}`);
      } finally {
        setUploads((u) => { const next = { ...u }; delete next[key]; return next; });
      }
    }
    refetchAnnotations();
  };

  // Presigned URLs expire (~1h) — refetch before every open so links are fresh.
  const openAttachment = async (annotationId: string, attachmentId: string, mode: 'view' | 'download') => {
    setAttachError('');
    const list = await refetchAnnotations();
    const fresh = list.find((x) => x.id === annotationId)?.attachments.find((x) => x.id === attachmentId);
    if (!fresh) { setAttachError('That file no longer exists'); return; }
    if (mode === 'download') {
      if (fresh.downloadUrl) window.location.assign(fresh.downloadUrl);
      else setAttachError(isShare ? 'This link does not allow downloads' : 'Downloads need S3 storage');
      return;
    }
    setViewing(fresh);
  };

  const removeAttachment = (annotationId: string, att: NoteAttachment) => {
    if (!window.confirm(`Remove "${att.fileName}" from this note?`)) return;
    deleteAttachment({ variables: { annotationId, attachmentId: att.id } });
  };

  // ── Measurements ──
  const toggleMeasure = (kind: MeasureKind) => {
    setAnnotationMode(false);
    setMeasureError('');
    setLastMeasure('');
    setMeasureKind((current) => (current === kind ? null : kind));
  };
  const stopMeasure = () => { setMeasureKind(null); setLastMeasure(''); };

  const draftMeasurement = (kind: MeasureKind, geometry: LineString | Polygon): Measurement | null => {
    const ol = olRef.current;
    if (!ol) return null;
    const createdAt = new Date().toISOString();
    if (kind === 'distance') {
      const line = geometry as LineString;
      const points = toMeasurementPoints(line.getCoordinates());
      const distance = ol.lengthOf(line);
      return points.length >= 2 && distance > 0
        ? { id: '', kind, start: points[0], end: points[points.length - 1], distance, createdAt }
        : null;
    }
    const polygon = geometry as Polygon;
    const points = toMeasurementPoints(polygon.getCoordinates()[0] ?? []);
    const area = ol.areaOf(polygon);
    return points.length >= 3 && area > 0 ? { id: '', kind, points, area, createdAt } : null;
  };

  const handleMeasured = async (kind: MeasureKind, geometry: LineString | Polygon) => {
    const draft = draftMeasurement(kind, geometry);
    if (!draft || !fileId) return;
    setLastMeasure(measurementValue(draft));
    if (isShare && !guestMeasures) {
      setLocalMeasures((list) => [...list, { ...draft, id: `local-${crypto.randomUUID()}` }]);
      return;
    }
    const saving = { ...draft, id: `saving-${crypto.randomUUID()}` };
    setSavingMeasures((list) => [...list, saving]);
    setMeasureError('');
    const guest = { token: shareToken, guestName: guestName.trim() || null };
    try {
      if (kind === 'distance') {
        const line = { fileId, start: draft.start, end: draft.end, distance: draft.distance };
        if (isShare) await sharedCreateMeasurement({ variables: { ...line, ...guest } });
        else await createMeasurement({ variables: line });
      } else {
        const area = { fileId, points: draft.points, area: draft.area };
        if (isShare) await sharedCreateAreaMeasurement({ variables: { ...area, ...guest } });
        else await createAreaMeasurement({ variables: area });
      }
      await refetchMeasurements();
    } catch (err) {
      setMeasureError(err instanceof Error ? err.message : 'Could not save the measurement');
      setMeasuresCollapsed(false);
    } finally {
      setSavingMeasures((list) => list.filter((m) => m !== saving));
    }
  };
  useEffect(() => { measuredRef.current = handleMeasured; });

  const selectMeasurement = (m: Measurement) => {
    setActiveMeasureId(m.id);
    const ol = olRef.current;
    const coords = measurementCoordinates(m);
    if (!ol || !coords) return;
    ol.map.getView().fit(coordinatesExtent(coords), {
      padding: [64, 64, 64, 64],
      duration: 300,
      minResolution: ol.nativeResolution,
    });
  };

  const toggleMeasureVisible = (id: string) => setHiddenMeasures((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const closeDelete = () => { setPendingDelete(null); setDeleteError(''); };
  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target) return;
    if (isLocalMeasure(target)) {
      setLocalMeasures((list) => list.filter((m) => m.id !== target.id));
      closeDelete();
      return;
    }
    setDeletingMeasure(true);
    setDeleteError('');
    try {
      if (isShare) await sharedDeleteMeasurement({ variables: { token: shareToken, measurementId: target.id } });
      else await deleteMeasurement({ variables: { measurementId: target.id } });
      await refetchMeasurements();
      closeDelete();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete the measurement');
    } finally {
      setDeletingMeasure(false);
    }
  };

  const measureHint = measureKind === 'area'
    ? lastMeasure
      ? `Area: ${lastMeasure} — outline another area, or press Done`
      : 'Click the corners of the area — click the first point or double-click to finish'
    : lastMeasure
      ? `Distance: ${lastMeasure} — click two new points to measure again`
      : 'Click a start point, then an end point';

  const pixelCm = meta?.geoTransform?.[1] ? Math.abs(meta.geoTransform[1]) * 100 : null;

  return (
    <div className={styles.container}>
      {!embedded && (
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <Icon name="arrow_back" style={{ fontSize: 16 }} />
              Back
            </button>
            <div className={styles.divider} />
            <div className={styles.fileInfo}>
              <Icon name="satellite_alt" className={styles.fileIcon} />
              <span className={styles.fileName}>{fileInfo?.originalName ?? 'Loading…'}</span>
              {fileInfo && (
                <span className={styles.fileMeta}>{formatSize(fileInfo.fileSize)}</span>
              )}
            </div>
          </div>
          <div className={styles.headerRight}>
            {meta?.width && meta?.height && (
              <span className={styles.metaChip}>
                {meta.width.toLocaleString()} × {meta.height.toLocaleString()} px
                {pixelCm ? ` · ${pixelCm.toFixed(1)} cm/px` : ''}
                {meta.crs ? ` · ${meta.crs}` : ''}
              </span>
            )}
          </div>
        </header>
      )}

      <div className={styles.body}>
        {/* ── Notes rail (SplatViewer design) ── */}
        <aside className={styles.rail}>
          <div
            className={`${styles.railSectionHeader} ${styles.railSubHeaderClickable}`}
            onClick={() => setNotesCollapsed((c) => !c)}
          >
            <span className={styles.railSectionLabel}>Notes</span>
            <span className={styles.railSectionCount}>{annotations.length}</span>
            {canWriteNotes && !annotationMode && !pendingPos && (
              <button
                className={styles.railMiniBtn}
                title="New note"
                disabled={stage !== 'ready'}
                onClick={(e) => { e.stopPropagation(); setNotesCollapsed(false); startAddNote(); }}
              >
                <Icon name="add" style={{ fontSize: 14 }} />
              </button>
            )}
          </div>

          {!notesCollapsed && (
            <>
              {annotationMode && (
                <div className={styles.noteForm}>
                  <div className={styles.noteHint}>
                    <Icon name="ads_click" style={{ fontSize: 14 }} />
                    Click on the map to place the note
                  </div>
                  <div className={styles.btnRow}>
                    <button className={styles.btnGhost} onClick={cancelAddNote}>Cancel</button>
                  </div>
                </div>
              )}

              {pendingPos && (
                <div className={styles.noteForm}>
                  <div className={styles.noteHint}>
                    <Icon name="location_on" style={{ fontSize: 14 }} />
                    Position captured — add a comment
                  </div>
                  {isShare && (
                    <input
                      className={styles.textarea}
                      placeholder="Your name (shown on the note)"
                      value={guestName}
                      maxLength={60}
                      onChange={(e) => updateGuestName(e.target.value)}
                    />
                  )}
                  <textarea
                    className={styles.textarea}
                    placeholder="Describe the issue or note…"
                    value={pendingText}
                    rows={3}
                    autoFocus
                    onChange={(e) => setPendingText(e.target.value)}
                  />
                  <div className={styles.btnRow}>
                    <button className={styles.btnGhost} onClick={() => { setPendingPos(null); setPendingText(''); }}>Cancel</button>
                    <div style={{ flex: 1 }} />
                    <button
                      className={styles.btnPrimary}
                      onClick={handleSaveAnnotation}
                      disabled={!pendingText.trim() || creating}
                    >
                      {creating ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {noteError && <p className={styles.attachError}>{noteError}</p>}
                </div>
              )}

              {annotations.length === 0 && !annotationMode && !pendingPos && (
                <p className={styles.railEmpty}>
                  {canWriteNotes ? 'No notes yet — use + to pin one on the map.' : 'No notes on this file yet.'}
                </p>
              )}

              {annotations.map((a, i) => (
                <div
                  key={a.id}
                  ref={a.id === activeId ? activeCardRef : null}
                  className={`${styles.noteCard} ${a.id === activeId ? styles.noteCardActive : ''} ${a.status === 'resolved' ? styles.noteCardResolved : ''}`}
                  onClick={() => handleCardClick(a.id)}
                >
                  <div className={styles.noteHead}>
                    <span className={styles.noteIdx}>{String(i + 1).padStart(2, '0')}</span>
                    <p className={styles.noteAuthor}>{authorName(a)}</p>
                    {a.attachments.length > 0 && (
                      <span className={styles.noteAttachChip}>
                        <Icon name="attach_file" style={{ fontSize: 11 }} />
                        {a.attachments.length}
                      </span>
                    )}
                    {a.status === 'resolved' && <span className={styles.noteResolvedChip}>resolved</span>}
                    <p className={styles.noteTime}>{formatTime(a.createdAt)}</p>
                  </div>
                  <p className={styles.noteText}>{a.text}</p>

                  {a.replies.length > 0 && (
                    <div className={styles.noteReplies}>
                      {a.replies.map((r) => (
                        <div key={r.id}>
                          <p className={styles.replyAuthor}>{authorName(r)}</p>
                          <p className={styles.replyText}>{r.text}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {expandedId === a.id && (
                    <div className={styles.noteActions} onClick={(e) => e.stopPropagation()}>
                      <div className={styles.attachBlock}>
                        <div className={styles.attachHeader}>
                          <span className={styles.attachLabel}>Files</span>
                          {!isShare && (
                            <button
                              className={styles.attachAddBtn}
                              onClick={() => { attachTargetRef.current = a.id; attachInputRef.current?.click(); }}
                            >
                              <Icon name="attach_file" style={{ fontSize: 12 }} />
                              Attach PDF
                            </button>
                          )}
                        </div>
                        {a.attachments.map((att) => (
                          <div key={att.id} className={styles.attachRow}>
                            <Icon name={isPdfAttachment(att) ? 'picture_as_pdf' : 'draft'} className={styles.attachIcon} />
                            <button className={styles.attachName} onClick={() => openAttachment(a.id, att.id, 'view')}>
                              {att.fileName}
                            </button>
                            <span className={styles.attachSize}>{formatBytes(att.fileSize)}</span>
                            {att.downloadUrl && (
                              <button
                                className={styles.attachIconBtn}
                                title="Download"
                                onClick={() => openAttachment(a.id, att.id, 'download')}
                              >
                                <Icon name="download" style={{ fontSize: 14 }} />
                              </button>
                            )}
                            {!isShare && (
                              <button
                                className={styles.attachIconBtn}
                                title="Remove"
                                onClick={() => removeAttachment(a.id, att)}
                              >
                                <Icon name="close" style={{ fontSize: 14 }} />
                              </button>
                            )}
                          </div>
                        ))}
                        {Object.entries(uploads).filter(([, u]) => u.annotationId === a.id).map(([k, u]) => (
                          <div key={k} className={styles.attachUpload}>
                            <span className={styles.attachUploadName}>{u.name}</span>
                            <div className={styles.attachProgress}>
                              <div className={styles.attachProgressFill} style={{ width: `${u.pct}%` }} />
                            </div>
                          </div>
                        ))}
                        {a.attachments.length === 0
                          && Object.values(uploads).every((u) => u.annotationId !== a.id) && (
                          <p className={styles.attachEmpty}>{isShare ? 'No files attached' : 'No PDFs attached yet'}</p>
                        )}
                        {attachError && <p className={styles.attachError}>{attachError}</p>}
                      </div>

                      {noteError && <p className={styles.attachError}>{noteError}</p>}
                      {canWriteNotes && (
                        <>
                          {isShare && (
                            <input
                              className={styles.textarea}
                              placeholder="Your name (shown on the reply)"
                              value={guestName}
                              maxLength={60}
                              onChange={(e) => updateGuestName(e.target.value)}
                            />
                          )}
                          <textarea
                            className={styles.textarea}
                            placeholder="Add a reply…"
                            value={replyText}
                            rows={2}
                            onChange={(e) => setReplyText(e.target.value)}
                          />
                          <div className={styles.btnRow}>
                            {!isShare && (
                              <button
                                className={styles.btnGhost}
                                onClick={() => resolveAnnotation({ variables: { annotationId: a.id } })}
                              >
                                {a.status === 'resolved' ? 'Reopen' : 'Resolve'}
                              </button>
                            )}
                            <div style={{ flex: 1 }} />
                            {!isShare && (
                              <button
                                className={styles.btnDanger}
                                title="Delete"
                                onClick={() => deleteAnnotation({ variables: { annotationId: a.id } })}
                              >
                                <Icon name="delete" style={{ fontSize: 14 }} />
                              </button>
                            )}
                            <button
                              className={styles.btnPrimary}
                              onClick={() => handleReply(a.id)}
                              disabled={!replyText.trim() || replying}
                            >
                              Reply
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          <div
            className={`${styles.railSectionHeader} ${styles.railSubHeaderClickable} ${styles.railSectionDivider}`}
            onClick={() => setMeasuresCollapsed((c) => !c)}
          >
            <span className={styles.railSectionLabel}>Measurements</span>
            <span className={styles.railSectionCount}>{measurements.length}</span>
          </div>

          {!measuresCollapsed && (
            <>
              {isShare && !guestMeasures && (
                <p className={styles.measureNotice}>
                  <Icon name="info" style={{ fontSize: 13 }} />
                  Measurements you add on a shared link are not saved
                </p>
              )}
              {measureError && <p className={styles.measureError}>{measureError}</p>}

              {measurements.length === 0 && savingMeasures.length === 0 && (
                <p className={styles.railEmpty}>No measurements yet — use Distance or Area on the map.</p>
              )}

              {measurements.map((m) => {
                const hidden = hiddenMeasures.has(m.id);
                const isArea = m.kind === 'area';
                return (
                  <div
                    key={m.id}
                    ref={m.id === activeMeasureId ? activeMeasureCardRef : null}
                    className={[
                      styles.measureCard,
                      m.id === activeMeasureId ? styles.measureCardActive : '',
                      hidden ? styles.measureCardHidden : '',
                    ].filter(Boolean).join(' ')}
                    title={isArea ? 'Zoom to area' : 'Zoom to measurement'}
                    onClick={() => selectMeasurement(m)}
                  >
                    <span className={styles.measureIcon}>
                      <Icon name={isArea ? 'square_foot' : 'straighten'} style={{ fontSize: 15 }} />
                    </span>
                    <div className={styles.measureBody}>
                      <p className={styles.measureValue}>{measurementLabel(m)}</p>
                      <p className={styles.measureMeta}>{measureMeta(m)}</p>
                    </div>
                    <button
                      className={styles.measureIconBtn}
                      title={hidden ? 'Show' : 'Hide'}
                      onClick={(e) => { e.stopPropagation(); toggleMeasureVisible(m.id); }}
                    >
                      <Icon name={hidden ? 'visibility_off' : 'visibility'} style={{ fontSize: 15 }} />
                    </button>
                    {canDeleteMeasurement(m) && (
                      <button
                        className={styles.measureIconBtn}
                        title="Delete"
                        onClick={(e) => { e.stopPropagation(); setDeleteError(''); setPendingDelete(m); }}
                      >
                        <Icon name="delete" style={{ fontSize: 15 }} />
                      </button>
                    )}
                  </div>
                );
              })}

              {savingMeasures.map((m) => (
                <div key={m.id} className={`${styles.measureCard} ${styles.measureCardSaving}`}>
                  <span className={styles.measureIcon}>
                    <Icon name={m.kind === 'area' ? 'square_foot' : 'straighten'} style={{ fontSize: 15 }} />
                  </span>
                  <div className={styles.measureBody}>
                    <p className={styles.measureValue}>{measurementLabel(m)}</p>
                    <p className={styles.measureMeta}>Saving…</p>
                  </div>
                </div>
              ))}
            </>
          )}

          {!isShare && (
            <input
              ref={attachInputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              hidden
              onChange={(e) => {
                const target = attachTargetRef.current;
                if (target && e.target.files?.length) uploadAttachments(target, e.target.files);
                e.target.value = '';
              }}
            />
          )}
        </aside>

        <div className={styles.viewerBody}>
          <div ref={mapEl} className={styles.mapCanvas} onContextMenu={(e) => e.preventDefault()} />

          {stage === 'ready' && (
            <div className={styles.rotateControls}>
              <button
                className={styles.rotateBtn}
                title="Rotate left (you can also rotate with right-button drag, or Alt+Shift+drag)"
                onClick={() => rotateBy(-Math.PI / 12)}
              >
                <Icon name="rotate_left" style={{ fontSize: 17 }} />
              </button>
              <button
                className={styles.rotateBtn}
                title={rotationDeg ? `Reset north (${Math.round(rotationDeg)}°)` : 'North'}
                onClick={resetRotation}
              >
                {/* Same convention as ol's Rotate control: needle turns with the raw view rotation. */}
                <Icon name="navigation"
                  className={styles.rotateNeedle}
                  style={{ transform: `rotate(${rotationDeg}deg)` }}
                />
              </button>
              <button
                className={styles.rotateBtn}
                title="Rotate right (you can also rotate with right-button drag, or Alt+Shift+drag)"
                onClick={() => rotateBy(Math.PI / 12)}
              >
                <Icon name="rotate_right" style={{ fontSize: 17 }} />
              </button>
            </div>
          )}

          {stage === 'ready' && (
            <div className={styles.measureTools}>
              <button
                className={`${styles.measureToolBtn} ${measureKind === 'distance' ? styles.measureToolBtnActive : ''}`}
                title={measureKind === 'distance' ? 'Stop measuring' : 'Measure a distance — click two points'}
                onClick={() => toggleMeasure('distance')}
              >
                <Icon name="straighten" style={{ fontSize: 16 }} />
                Distance
              </button>
              <button
                className={`${styles.measureToolBtn} ${measureKind === 'area' ? styles.measureToolBtnActive : ''}`}
                title={measureKind === 'area' ? 'Stop measuring' : 'Measure an area — click its corners'}
                onClick={() => toggleMeasure('area')}
              >
                <Icon name="square_foot" style={{ fontSize: 16 }} />
                Area
              </button>
            </div>
          )}

          {measureKind && (
            <div className={styles.measureBanner}>
              <Icon name={measureKind === 'area' ? 'square_foot' : 'straighten'} style={{ fontSize: 16 }} />
              <span className={styles.measureBannerText}>{measureHint}</span>
              <button className={styles.measureBannerBtn} onClick={stopMeasure}>Done</button>
            </div>
          )}

          {annotationMode && (
            <div className={styles.annotModeBanner}>
              <Icon name="ads_click" style={{ fontSize: 16 }} />
              Click on the map to place your note — Esc or Cancel to stop
            </div>
          )}

          {(stage === 'checking' || stage === 'loading') && (
            <div className={styles.stateOverlay}>
              <div className={styles.stateIcon}>
                <Icon name="satellite_alt" style={{ fontSize: 34, color: '#0061ff' }} />
              </div>
              <p className={styles.stateTitle}>{stage === 'loading' ? 'Opening orthomosaic…' : 'Checking file…'}</p>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} />
              </div>
            </div>
          )}

          {stage === 'processing' && (
            <div className={styles.stateOverlay}>
              <div className={styles.stateIcon}>
                <Icon name="autorenew" style={{ fontSize: 34, color: '#0061ff' }} />
              </div>
              <p className={styles.stateTitle}>Preparing orthomosaic…</p>
              <p className={styles.stateSub}>
                {progress?.stage ?? 'Converting'}{typeof progress?.pct === 'number' ? ` — ${progress.pct}%` : ''}
              </p>
              <p className={styles.stateHint}>
                Large surveys take a few minutes. This page updates automatically.
              </p>
            </div>
          )}

          {stage === 'failed' && (
            <div className={styles.stateOverlay}>
              <div className={styles.stateIcon}>
                <Icon name="error" style={{ fontSize: 34, color: '#dc2626' }} />
              </div>
              <p className={styles.stateTitle}>Failed to Load</p>
              <div className={styles.errorBox}>{error}</div>
              <button className={styles.actionButton} onClick={() => window.location.reload()}>
                <Icon name="refresh" style={{ fontSize: 15 }} />
                Retry
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── PDF lightbox (SplatViewer design) ── */}
      {viewing && (
        <div className={styles.lightbox} onClick={() => setViewing(null)}>
          <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
            <div className={styles.lightboxBar}>
              <Icon name="picture_as_pdf" className={styles.attachIcon} />
              <span className={styles.lightboxName}>
                {viewing.fileName}
                <span className={styles.attachSize}>
                  {' '}· {formatBytes(viewing.fileSize)}
                  {viewing.uploadedBy ? ` · ${viewing.uploadedBy.firstName} ${viewing.uploadedBy.lastName}` : ''}
                </span>
              </span>
              {viewing.downloadUrl && (
                <a className={styles.lightboxBtn} href={viewing.downloadUrl}>
                  <Icon name="download" style={{ fontSize: 15 }} />
                  Download
                </a>
              )}
              <button className={styles.lightboxBtn} onClick={() => setViewing(null)} aria-label="Close">
                <Icon name="close" style={{ fontSize: 15 }} />
              </button>
            </div>
            <div className={styles.lightboxBody}>
              {viewing.url
                ? <iframe className={styles.lightboxFrame} src={viewing.url} title={viewing.fileName} />
                : <p className={styles.lightboxNoPreview}>Preview needs S3 storage.</p>}
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!pendingDelete}
        onClose={closeDelete}
        title="Delete Measurement"
        footer={
          <>
            <button onClick={closeDelete} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">Cancel</button>
            <button onClick={confirmDelete} disabled={deletingMeasure} className="px-5 py-2 rounded-lg text-sm font-medium bg-error text-white hover:opacity-90 disabled:opacity-40">Delete</button>
          </>
        }
      >
        <p className="text-sm text-on-surface-variant">
          Are you sure you want to delete this measurement{pendingDelete ? ` (${measurementLabel(pendingDelete)})` : ''}? This action cannot be undone.
        </p>
        {deleteError && <p className="text-sm text-error mt-3">{deleteError}</p>}
      </Modal>
    </div>
  );
};

export default OrthoViewer;
